import React, { useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import moment from 'moment-timezone';
import LoanForm from './LoanForm';
import { db } from './firebaseConfig';
import { collection, updateDoc, doc, onSnapshot } from 'firebase/firestore';
import { Modal, Box, Container, Typography, Card, CardContent, Chip, Pagination } from '@mui/material';
import EventNoteIcon from '@mui/icons-material/EventNote';
import AlertComponent from './AlertComponent';
import './Calendar.css';

const Calendar = ({ user }) => {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventModalIsOpen, setEventModalIsOpen] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', type: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const eventsPerPage = 5;

  const handleAlert = (message, type) => {
    setAlert({ open: true, message, type });
  };

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'events'), (snapshot) => {
      const eventsData = snapshot.docs.map(doc => ({
        id: doc.id,
        title: doc.data().extendedProps.clientName,
        start: doc.data().start.toDate(),
        end: doc.data().start.toDate(),
        extendedProps: doc.data().extendedProps
      }));
      setEvents(eventsData);
    });

    return () => unsubscribe();
  }, []);

  const openEventModal = (event) => {
      setSelectedEvent(event);
      setEventModalIsOpen(true);
  };

  const closeEventModal = () => setEventModalIsOpen(false);

  const handleDateClick = (arg) => {
    if (user.role !== 'readonly') {
      const currentDateTime = moment(arg.date).tz('America/Puerto_Rico').format();
      setSelectedEvent({
        title: '',
        start: currentDateTime,
        end: currentDateTime,
        allDay: true,
      });
      setEventModalIsOpen(true);
    } else {
      handleAlert('No tienes permisos para crear eventos.', 'warning');
    }
  };

  const handleEventClick = (info) => {
    openEventModal(info.event);
  };

  const handleEventDrop = async (info) => {
    if (user.role !== 'readonly') {
      const { event } = info;
      const eventDoc = doc(db, 'events', event.id);
      await updateDoc(eventDoc, { start: event.start });
      setEvents(events.map(e => e.id === event.id ? { ...e, start: event.start } : e));
    } else {
      handleAlert('No tienes permisos para mover eventos.', 'warning');
    }
  };

  const futureEvents = events.filter(event => new Date(event.start) >= new Date())
    .sort((a, b) => new Date(a.start) - new Date(b.start));

  const indexOfLastEvent = currentPage * eventsPerPage;
  const indexOfFirstEvent = indexOfLastEvent - eventsPerPage;
  const currentEvents = futureEvents.slice(indexOfFirstEvent, indexOfLastEvent);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  return (
    <Container sx={{ mt: 9 }}>
      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay'
        }}
        events={events}
        eventClick={handleEventClick}
        dateClick={handleDateClick}
        eventDrop={handleEventDrop}
        editable={true}
        droppable={user.role !== 'readonly'}
        displayEventTime={false}
        height="auto"
        aspectRatio={1.0}
      />

      <Box sx={{ mt: 4, backgroundColor: '#f0f4f8', p: 2, borderRadius: 2, boxShadow: 3, mb: 4 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold', color: '#0b6fae' }}>Próximos Eventos</Typography>

        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
          {currentEvents.map(event => (
            <Card key={event.id} onClick={() => openEventModal(event)} sx={{ cursor: 'pointer', transition: '0.3s', '&:hover': { boxShadow: 6, transform: 'translateY(-5px)' } }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <EventNoteIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{event.title}</Typography>
                </Box>
                <Chip label={moment(event.start).format('DD/MM/YYYY HH:mm')} color="secondary" size="small" />
              </CardContent>
            </Card>
          ))}
        </Box>

        <Pagination
          count={Math.ceil(futureEvents.length / eventsPerPage)}
          page={currentPage}
          onChange={handlePageChange}
          sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}
        />
      </Box>

      <Modal open={eventModalIsOpen} onClose={closeEventModal}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: { xs: '90%', sm: 400 }, height: { xs: '90%', sm: 'auto' }, overflowY: 'auto', bgcolor: 'background.paper', boxShadow: 24, p: 4 }}>
          <LoanForm user={user} selectedDate={selectedEvent ? selectedEvent.start : null} closeModal={closeEventModal} isOpen={eventModalIsOpen} selectedEvent={selectedEvent}
            onAlert={handleAlert} />
        </Box>
      </Modal>

      <AlertComponent
        open={alert.open}
        message={alert.message}
        type={alert.type}
        handleClose={() => setAlert({ ...alert, open: false })}
      />
    </Container>
  );
};

export default Calendar;
